import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../lib/auth";
import { getMyTickets, getFestivalOrderReceipt, getTicketSessionReceipt } from "../lib/api";
import { usePurchaseEvent } from "../lib/analytics";
import { PageSkeleton } from "../components/skeleton";

export function TicketsPage() {
  const { user, isLoading: authLoading } = useAuth();

  // Just redirected here from Stripe checkout (?purchased=<id>&kind=...). The
  // webhook that issues the ticket is async and may not have landed yet.
  // /tickets receives two different flows with two different id shapes,
  // `kind` disambiguates which receipt endpoint to poll for the `purchase`
  // dataLayer event (see usePurchaseEvent in lib/analytics.ts).
  const purchasedParams =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const justPurchased = !!purchasedParams?.get("purchased");
  const purchasedId = purchasedParams?.get("purchased") ?? null;
  const purchasedKind = purchasedParams?.get("kind");
  usePurchaseEvent(
    purchasedId,
    purchasedKind === "ticket-session" ? getTicketSessionReceipt : getFestivalOrderReceipt,
  );

  const ticketsQuery = useQuery({
    queryKey: ["my-tickets"],
    queryFn: getMyTickets,
    enabled: !!user,
    // Cache fallback in api.ts means we can serve from Dexie when offline.
    retry: 1,
    // After a purchase the wallet can be momentarily empty while the Stripe
    // webhook issues the ticket. Poll every 2s for up to ~7 tries (~15s) while
    // the list is still empty, then stop, react-query halts automatically once
    // tickets appear (list non-empty) or the attempt budget is spent, so we
    // never spin forever. A single-shot refetch missed slow/cold webhooks.
    refetchInterval: (query) => {
      if (!justPurchased) return false;
      const data = query.state.data as unknown[] | undefined;
      const empty = !data || data.length === 0;
      return empty && query.state.dataUpdateCount < 8 ? 2000 : false;
    },
  });

  // Show an offline banner when navigator.onLine flips false. Mirrors
  // the same indicator on the ticket-detail page.
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  if (authLoading) {
    return <PageSkeleton />;
  }

  if (!user) {
    // Guests have no wallet. If they just paid (guest checkout returns here),
    // confirm the purchase + point them at their email rather than showing a
    // bare "sign in" prompt that reads like the payment failed.
    return (
      <div className="space-y-6">
        {justPurchased ? (
          <div className="rounded-[var(--radius)] border border-[var(--venue-accent)]/40 bg-[var(--venue-accent)]/10 px-4 py-3 text-sm text-[var(--foreground)]">
            You’re in! Your order is confirmed, your tickets are on their way to your email.
          </div>
        ) : null}
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]">My Tickets</h1>
          <p className="mt-1 text-sm text-[var(--muted)]">
            {justPurchased
              ? "Create an account with the email you used at checkout to manage your tickets here."
              : "Sign in to see your tickets."}
          </p>
        </div>
        <Link
          to={justPurchased ? "/sign-up" : "/sign-in"}
          className="inline-block rounded-[var(--radius)] bg-[var(--venue-accent)] px-4 py-2 text-sm font-medium text-[var(--foreground)] transition hover:opacity-90"
        >
          {justPurchased ? "Create account" : "Sign in"}
        </Link>
      </div>
    );
  }

  const tickets = ticketsQuery.data ?? [];
  const now = Date.now();
  const upcomingTickets = tickets.filter(
    (t) => new Date(t.assembly.date).getTime() >= now,
  );
  const pastTickets = tickets.filter(
    (t) => new Date(t.assembly.date).getTime() < now,
  );

  return (
    <div className="space-y-6">
      {!online ? (
        <div className="rounded-[var(--radius)] border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          Offline, showing cached tickets.
        </div>
      ) : null}

      {justPurchased ? (
        <div className="rounded-[var(--radius)] border border-[var(--venue-accent)]/40 bg-[var(--venue-accent)]/10 px-4 py-3 text-sm text-[var(--foreground)]">
          {tickets.length === 0
            ? "Payment received, finalising your order. Your tickets will appear here in a moment…"
            : "You’re in! Your order is confirmed, your tickets are below (and on their way to your email)."}
        </div>
      ) : null}

      <div>
        <h1 className="text-2xl font-bold text-[var(--foreground)]">My Tickets</h1>
        <p className="mt-1 text-sm text-[var(--muted)]">Your tickets</p>
      </div>

      {tickets.length === 0 ? (
        <div className="rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-8 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="mx-auto h-12 w-12 text-[var(--muted)]">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 0 1 0 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 0 1 0-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375Z" />
          </svg>
          <p className="mt-4 text-[var(--muted)]">No tickets yet</p>
          <Link
            to="/events"
            className="mt-4 inline-block rounded-[var(--radius)] bg-[var(--venue-accent)] px-4 py-2 text-sm font-medium text-[var(--foreground)] transition hover:opacity-90"
          >
            Browse events
          </Link>
        </div>
      ) : (
        <>
          {upcomingTickets.length > 0 && (
            <section>
              <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-[var(--muted)]">Upcoming</h2>
              <div className="space-y-3">
                {upcomingTickets.map((ticket) => (
                  <Link
                    key={ticket.id}
                    to="/tickets/$id"
                    params={{ id: ticket.id }}
                    className="flex gap-4 rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-4 transition hover:border-[var(--border-strong)] hover:bg-[var(--surface-2)]"
                  >
                    <div className="flex h-16 w-16 items-center justify-center rounded-[var(--radius)] bg-[var(--surface-2)]">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6 text-[var(--muted)]">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 0 1 0 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 0 1 0-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375Z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-[var(--foreground)]">{ticket.assembly.title}</p>
                      <p className="text-sm text-[var(--muted)]">{ticket.tier || "General"}</p>
                      <p className="mt-1 text-xs text-[var(--muted)]">
                        {new Date(ticket.assembly.date).toLocaleDateString("en-GB", {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 self-center text-[var(--foreground)]">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                    </svg>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {pastTickets.length > 0 && (
            <section>
              <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-[var(--muted)]">Past</h2>
              <div className="space-y-3">
                {pastTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="flex gap-4 rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-4 opacity-60"
                  >
                    <div className="flex h-16 w-16 items-center justify-center rounded-[var(--radius)] bg-[var(--surface-2)]">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6 text-[var(--muted)]">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 0 1 0 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 0 1 0-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375Z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-[var(--foreground)]">{ticket.assembly.title}</p>
                      <p className="text-sm text-[var(--muted)]">{ticket.tier || "General"}</p>
                      <p className="mt-1 text-xs text-[var(--muted)]">
                        {new Date(ticket.assembly.date).toLocaleDateString("en-GB", {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
